/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useMemo } from "react";
import { useTheme } from "styled-components";

//internal-deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { generateCustomTheme } from "@swiggy-private/checkout-core";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";

//constants
import {
    PAY_TEXT,
    PAY_INSTANTLY_SUBTEXT_TURBO_WIDGET,
    SLIDE_TO_PAY_AMOUNT,
    POP_COLORS,
} from "@payment-ui/constants/TurboPayment";

//styles
import { BoltText, BoltWrapper, DiscountAmount, PayButton } from "./styles";

//helpers
import { format } from "@swiggy-private/js-utils";

interface TurboV2PaymentButtonProps {
    finalAmount: string;
    discountAmount?: string;
    showBolt?: boolean;
    onPayClick: VoidFunction;
    dataTestId?: string;
    isPop?: boolean;
}

function TurboV2PaymentButton({
    finalAmount,
    onPayClick,
    discountAmount,
    showBolt,
    dataTestId,
    isPop,
}: TurboV2PaymentButtonProps) {
    const theme = useTheme();
    const [customThemeOverrideForPop, customThemeVariables] = useMemo(
        () =>
            generateCustomTheme(theme, {
                positive: theme.colors[theme.colorMode].special_positive,
                ...(isPop && {
                    positive: POP_COLORS.PRIMARY,
                    text_Button_Primary: POP_COLORS.TEXT_PRIMARY,
                    text_inverse_Highest_Emphasis: POP_COLORS.TEXT_PRIMARY,
                    text_inverse_Med_Emphasis: POP_COLORS.TEXT_PRIMARY,
                }),
            }),
        [isPop, theme],
    );
    return (
        <ThemeProvider
            theme={customThemeOverrideForPop}
            colorMode={theme.colorMode}
            design_language={theme.designLanguage}
        >
            <div style={customThemeVariables}>
                <PayButton variant="positive" onClick={onPayClick} data-testid={dataTestId} isPop={isPop}>
                    <Text
                        data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON_TEXT}
                        variant="V2.body1.neutral.bold"
                        color="text_Button_Primary"
                    >
                        {PAY_TEXT}{" "}
                        {discountAmount && discountAmount !== finalAmount && (
                            <Text
                                data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON_DISCOUNT}
                                variant="V2.overline.neutral.medium"
                                color="text_inverse_Med_Emphasis"
                                as="span"
                            >
                                <DiscountAmount>{format(SLIDE_TO_PAY_AMOUNT, discountAmount)}</DiscountAmount>
                            </Text>
                        )}
                        {format(SLIDE_TO_PAY_AMOUNT, finalAmount)}
                    </Text>
                    {showBolt && (
                        <BoltWrapper data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON_BOLT}>
                            <SpriteIconWeb iconName="boltFilled12" fillColor="text_inverse_Med_Emphasis" />
                            <BoltText
                                data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON_BOLT_TEXT}
                                variant="V2.overline.neutral.bold_spaced"
                            >
                                {PAY_INSTANTLY_SUBTEXT_TURBO_WIDGET}
                            </BoltText>
                        </BoltWrapper>
                    )}
                </PayButton>
            </div>
        </ThemeProvider>
    );
}

export default memo(TurboV2PaymentButton);
